import React from 'react';
import { WebsiteDesignProps } from './types';
import { FileText, BarChart, BookOpen, Quote } from 'lucide-react';
import { Button } from "@/components/ui/button";

const ScientificJournal: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "The Research Institute";

    return (
        <div className="bg-white min-h-screen font-serif text-black leading-relaxed">
            <style>{`
                .column-layout {
                    column-count: 2;
                    column-gap: 2rem;
                }
                @media (max-width: 768px) {
                    .column-layout {
                        column-count: 1;
                    }
                }
            `}</style>

            <header className="border-b-2 border-black p-8 text-center max-w-5xl mx-auto mt-8">
                <div className="uppercase tracking-widest text-xs font-bold mb-4">Vol. 24 • No. 1 • 2026</div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 font-serif">
                    {schoolName}
                </h1>
                <div className="flex justify-center gap-8 text-sm italic font-serif border-y border-black py-2 max-w-2xl mx-auto">
                    <span>Est. 1950</span>
                    <span>•</span>
                    <span>Excellence in Inquiry</span>
                    <span>•</span>
                    <span>Peer Reviewed</span>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                     <div className="col-span-12 md:col-span-8">
                         <h2 className="text-2xl font-bold mb-4 uppercase text-xs tracking-widest">Abstract</h2>
                         <p className="mb-8 text-lg font-bold italic text-gray-700">
                             This institution aims to provide a rigorous, data-driven approach to education. Our findings indicate a high correlation between structured learning environments and student success.
                         </p>

                         <hr className="my-8 border-gray-300"/>

                         <h2 className="text-2xl font-bold mb-4 font-serif">1. Introduction</h2>
                         <div className="column-layout text-justify text-sm md:text-base">
                             <p className="mb-4">
                                 Education is the cornerstone of progress. At {schoolName}, we do not merely teach; we investigate, we hypothesize, and we conclude. Our methodology is rooted in the scientific method, ensuring that every student learns to think critically and objectively.
                             </p>
                             <p className="mb-4">
                                 <span className="font-bold">Figure 1.1</span> implies a steady growth in intellectual curiosity among our cohort. The data suggests that immersion in complex problem-solving scenarios yields significant cognitive benefits.
                             </p>
                             <div className="my-8 border border-black p-4 bg-gray-50 break-inside-avoid">
                                 <img src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop" className="w-full grayscale mb-2 h-48 object-cover" alt="Lab" />
                                 <div className="text-xs font-bold font-sans">Fig 1. Students engaging in practical application.</div>
                             </div>
                             <p>
                                 Furthermore, our faculty comprises leading experts in their respective fields, bringing real-world research experience directly into the classroom. This direct exposure to primary sources fosters a deeper level of engagement.
                             </p>
                         </div>
                     </div>

                     <div className="col-span-12 md:col-span-4 border-l border-gray-200 pl-8 hidden md:block">
                         <div className="mb-12 sticky top-8">
                             <h3 className="font-bold uppercase text-xs tracking-widest mb-6">Key Statistics</h3>
                             
                             <div className="mb-6">
                                 <div className="text-4xl font-bold font-serif mb-1">98%</div>
                                 <div className="text-xs text-gray-500 uppercase">University Placement</div>
                             </div>
                             <div className="mb-6">
                                 <div className="text-4xl font-bold font-serif mb-1">15:1</div>
                                 <div className="text-xs text-gray-500 uppercase">Student-Faculty Ratio</div>
                             </div>
                             
                             <Button className="w-full rounded-none bg-black text-white hover:bg-gray-800 uppercase text-xs tracking-widest h-12">
                                 Download Prospectus (PDF)
                             </Button>
                         </div>
                     </div>
                </div>

                <div className="mt-16 bg-gray-100 p-8 border border-gray-300">
                     <Quote className="mb-4 text-gray-400" />
                     <p className="font-serif italic text-xl text-gray-700">
                         "The school provided the rigorous framework I needed to excel in my doctoral studies."
                     </p>
                     <div className="mt-4 text-sm font-bold text-right">— Dr. Jane Doe, Alumna</div>
                </div>
            </main>
        </div>
    );
};

export default ScientificJournal;
